// Auto-generated by splitter.py — do not regenerate; edit directly.

import React, { useEffect, useState } from 'react';
import { Mail, RefreshCw, ShieldCheck, X } from 'lucide-react';
import { sendDashboardOTP, checkDashboardOTP } from '../lib/backOfficeApi';
import { toast } from '../lib/buses';
import { Button } from './Button';

// Step-up auth modal. Sends a 6-digit code to the user's email, lets them
// paste it, then hands the code back to the caller via `onVerified(otp)`.
// The caller is responsible for attaching the code to the protected request
// (the backend's requireDashboardOTP middleware consumes the OTP from the
// X-OTP header — see /Users/loft/tkt-backend src/middlewares/dashboardOTP.ts).

export const OtpPromptModal = ({ open, onClose, action = 'dashboard_action', title, sub, onVerified }) => {
  const [phase, setPhase] = useState('sending'); // 'sending' | 'ready' | 'verifying' | 'error'
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (!open) return undefined;
    setCode('');
    setError('');
    setPhase('sending');
    let cancelled = false;
    (async () => {
      try {
        // ALWAYS SEND. This used to short-circuit when the backend still held a valid code for
        // this action — it set phase 'ready' and returned WITHOUT sending, so the prompt said
        // "a 6-digit code is required to finalize this order" over an input while nothing had
        // been issued. Codes live 10 minutes; anyone who had not kept the earlier one (or never
        // saw it, or it was issued for a different action in another tab) had no way forward and
        // no way to know why. Reuse saved one email and cost the operator the whole action.
        //
        // A prompt that asks for a code we did not send is the same defect as a toast that says
        // "sent" when nothing was: the interface asserting something it did not do. Sending is
        // cheap, overwrites the stored code, and makes the sentence on screen true every time.
        const status = await checkDashboardOTP({ action }).catch(() => ({ hasValidOTP: false }));
        if (cancelled) return;
        setMaskedEmail(status?.email || '');
        await sendDashboardOTP({ action });
        if (cancelled) return;
        setPhase('ready');
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || 'Failed to send authorization code');
        setPhase('error');
      }
    })();
    return () => { cancelled = true; };
  }, [open, action]);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const id = setTimeout(() => setResendCooldown((value) => Math.max(0, value - 1)), 1000);
    return () => clearTimeout(id);
  }, [resendCooldown]);

  if (!open) return null;

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setError('');
    try {
      await sendDashboardOTP({ action });
      setResendCooldown(30);
      toast('Authorization code resent', 'info');
    } catch (err) {
      setError(err?.message || 'Failed to resend code');
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    const trimmed = code.trim();
    if (!/^\d{4,8}$/.test(trimmed)) {
      setError('Enter the numeric code from the email');
      return;
    }
    setError('');
    setPhase('verifying');
    onVerified?.(trimmed);
  };

  return (
    <div className="fixed inset-0 z-[95] bg-[#1F2937]/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-[#E6E4E1] rounded-[12px] w-full max-w-[440px] shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 pt-5 pb-4 border-b border-[#EEEDEA] flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-9 h-9 rounded-full bg-[#FEF3C7] text-[#8A5A12] flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-4 h-4" strokeWidth={2} />
            </div>
            <div className="min-w-0">
              <div className="text-[14.5px] font-semibold text-[#1F2937] leading-tight">{title || 'Confirm this action'}</div>
              {sub && <div className="text-[11.5px] text-[#6B7280] mt-0.5">{sub}</div>}
            </div>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-md hover:bg-[#F2F1ED] flex items-center justify-center flex-shrink-0" title="Close">
            <X className="w-3.5 h-3.5 text-[#6B7280]" strokeWidth={2} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="px-5 py-4 space-y-3">
            <div className="flex items-center gap-2 text-[12px] text-[#4B5563] bg-[#FBFAF7] border border-[#EEEDEA] rounded-md px-3 py-2">
              <Mail className="w-3.5 h-3.5 text-[#6B7280]" strokeWidth={1.75} />
              {phase === 'sending' && <span>Sending a 6-digit code to your email…</span>}
              {phase === 'ready' && <span>Enter the 6-digit code we sent to your email{maskedEmail ? ` (${maskedEmail})` : ''}.</span>}
              {phase === 'verifying' && <span>Authorizing…</span>}
              {phase === 'error' && <span className="text-[#9B1F1F]">{error}</span>}
            </div>

            <div>
              <label className="block text-[10.5px] uppercase tracking-[0.06em] text-[#6B7280] font-semibold mb-1.5">Authorization code</label>
              <input
                autoFocus
                value={code}
                onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 8)); setError(''); }}
                disabled={phase === 'sending' || phase === 'verifying'}
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                placeholder="••••••"
                className="w-full h-11 px-3 text-[20px] font-mono tracking-[0.4em] text-center bg-white border border-[#E6E4E1] rounded-md focus:outline-none focus:border-[#1F2937] disabled:opacity-50"
              />
              {error && phase !== 'error' && <div className="text-[11px] text-[#9B1F1F] mt-1.5">{error}</div>}
            </div>

            <button
              type="button"
              onClick={handleResend}
              disabled={phase === 'sending' || phase === 'verifying' || resendCooldown > 0}
              className="text-[11.5px] text-[#4B5563] hover:text-[#1F2937] disabled:opacity-40 inline-flex items-center gap-1.5"
            >
              <RefreshCw className="w-3 h-3" strokeWidth={1.75} />
              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
            </button>
          </div>

          <div className="px-5 py-3 border-t border-[#EEEDEA] bg-[#FBFAF7] rounded-b-[12px] flex items-center justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
            <Button variant="primary" size="sm" type="submit" disabled={!code || phase === 'sending' || phase === 'verifying'}>
              {phase === 'verifying' ? 'Verifying…' : 'Authorize & continue'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
